/**
 * CANVMS API Module
 * Video Management System API functions
 *
 * NOTE: Access control will be handled by Keycloak - user_id params removed
 */

import canvmsApi from './apiClient';

// Types — consolidated in canvision.types.ts (single source of truth)
export type {
  VMSSite,
  VMSCamera,
  StreamInfo,
  Recording,
  RecordingPlaybackInfo,
  VMSEvent,
  EventCounts,
  EventFilters,
  CrossSiteEventFilters,
  ConnectionTestResult,
  HealthStatus,
  DashboardSiteSummary,
  DashboardRecentEvent,
  DashboardData,
  Customer,
  CustomerAddress,
  UpdateCameraData,
  VMSUser,
  SiteAccessRecord,
  PTZCommand,
  CameraSetCamera,
  CameraSet,
  CameraSetCreateInput,
  CameraSetUpdateInput,
  CameraForSelection,
} from 'containers/pages/CANvision/types/canvision.types';

import type {
  VMSSite,
  VMSCamera,
  StreamInfo,
  Recording,
  RecordingPlaybackInfo,
  VMSEvent,
  EventCounts,
  EventFilters,
  CrossSiteEventFilters,
  ConnectionTestResult,
  HealthStatus,
  DashboardSiteSummary,
  DashboardRecentEvent,
  DashboardData,
  Customer,
  CustomerAddress,
  UpdateCameraData,
  VMSUser,
  SiteAccessRecord,
  PTZCommand,
  CameraSetCamera,
  CameraSet,
  CameraSetCreateInput,
  CameraSetUpdateInput,
  CameraForSelection,
} from 'containers/pages/CANvision/types/canvision.types';

// Sites API
export const getSites = async (): Promise<{ data: { sites: VMSSite[] } }> => {
  const { data } = await canvmsApi.get('/sites');
  return data;
};

export const getSite = async (siteId: number): Promise<{ data: VMSSite }> => {
  const { data } = await canvmsApi.get(`/sites/${siteId}`);
  return data;
};

export const createSite = async (siteData: {
  name: string;
  display_name?: string;
  description?: string;
  customer_id?: number;
  base_url: string;
}): Promise<{ data: VMSSite }> => {
  const { data } = await canvmsApi.post('/sites', siteData);
  return data;
};

export const updateSite = async (siteId: number, siteData: Partial<{
  name: string;
  display_name: string;
  description: string;
  customer_id: number;
  base_url: string;
  enabled: boolean;
}>): Promise<{ data: VMSSite }> => {
  const { data } = await canvmsApi.put(`/sites/${siteId}`, siteData);
  return data;
};

export const deleteSite = async (siteId: number): Promise<{ data: { message: string; site: VMSSite } }> => {
  const { data } = await canvmsApi.delete(`/sites/${siteId}`);
  return data;
};

export const testSiteConnection = async (_connectionData: {
  base_url: string;
}): Promise<{ data: ConnectionTestResult }> => {
  console.warn('[CANvmsAPI] testSiteConnection: endpoint removed — pending VMS migration');
  return Promise.reject(new Error('Endpoint removed — pending VMS migration'));
};

export const retrySiteConnection = async (_siteId: number): Promise<{ data: {
  previous_status: string;
  new_status: string;
  test_result: ConnectionTestResult;
} }> => {
  console.warn('[CANvmsAPI] retrySiteConnection: endpoint removed — pending VMS migration');
  return Promise.reject(new Error('Endpoint removed — pending VMS migration'));
};

export const syncSiteCameras = async (_siteId: number): Promise<{ data: {
  message: string;
  synced: number;
  errors: number;
  total: number;
} }> => {
  console.warn('[CANvmsAPI] syncSiteCameras: endpoint removed — pending VMS migration');
  return Promise.reject(new Error('Endpoint removed — pending VMS migration'));
};

// Cameras API
export const getCameras = async (siteId: number): Promise<{ data: { cameras: VMSCamera[] } }> => {
  const { data } = await canvmsApi.get(`/sites/${siteId}/cameras`);
  return data;
};

export const getAllCameras = async (siteId: number): Promise<{ data: { cameras: VMSCamera[] } }> => {
  const { data } = await canvmsApi.get(`/sites/${siteId}/cameras/all`);
  return data;
};

export const updateCamera = async (
  siteId: number,
  cameraId: number,
  data: UpdateCameraData
): Promise<{ data: VMSCamera }> => {
  const { data: response } = await canvmsApi.put(`/sites/${siteId}/cameras/${cameraId}`, data);
  return response;
};

export interface CreateCameraData {
  name: string;
  type: string;            // "h264" | "mjpeg" | "jpeg"
  protocol: string;        // "rtsp" | "http"
  host: string;
  port: string;
  path: string;
  username?: string;
  password?: string;
  latitude?: number;
  longitude?: number;
  customer_id?: number;
  address_id?: number;
}

export const createCamera = async (
  _siteId: number,
  _cameraData: CreateCameraData
): Promise<{ data: VMSCamera }> => {
  console.warn('[CANvmsAPI] createCamera: endpoint removed — pending VMS migration');
  return Promise.reject(new Error('Endpoint removed — pending VMS migration'));
};

export const deleteCamera = async (
  _siteId: number,
  _cameraId: number
): Promise<{ data: { message: string; camera_id: number } }> => {
  console.warn('[CANvmsAPI] deleteCamera: endpoint removed — pending VMS migration');
  return Promise.reject(new Error('Endpoint removed — pending VMS migration'));
};

export const getStreamUrl = async (_siteId: number, _cameraId: number, _type: 'hls' | 'mjpeg' = 'hls'): Promise<{ data: StreamInfo }> => {
  console.warn('[CANvmsAPI] getStreamUrl: endpoint removed — pending VMS migration');
  return Promise.reject(new Error('Endpoint removed — pending VMS migration'));
};

// Health API
export const getHealth = async (): Promise<{ data: HealthStatus }> => {
  const { data } = await canvmsApi.get('/health');
  return data;
};

export const getDashboard = async (): Promise<{ data: DashboardData }> => {
  const { data } = await canvmsApi.get('/dashboard');
  return data;
};

// Recordings API
export const getRecordings = async (
  _siteId: number,
  _cameraId: number,
  _startDate?: string,
  _endDate?: string
): Promise<{ data: { recordings: Recording[] } }> => {
  console.warn('[CANvmsAPI] getRecordings: endpoint removed — pending VMS migration');
  return Promise.reject(new Error('Endpoint removed — pending VMS migration'));
};

export const getRecordingUrl = async (
  _siteId: number,
  _cameraId: number,
  _filename: string
): Promise<{ data: RecordingPlaybackInfo }> => {
  console.warn('[CANvmsAPI] getRecordingUrl: endpoint removed — pending VMS migration');
  return Promise.reject(new Error('Endpoint removed — pending VMS migration'));
};

export const getSnapshotUrl = async (
  _siteId: number,
  _cameraId: number
): Promise<{ data: { snapshot_url: string } }> => {
  console.warn('[CANvmsAPI] getSnapshotUrl: endpoint removed — pending VMS migration');
  return Promise.reject(new Error('Endpoint removed — pending VMS migration'));
};

export const getDownloadUrl = async (
  _siteId: number,
  _cameraId: number,
  _filename: string
): Promise<{ data: { download_url: string; filename: string } }> => {
  console.warn('[CANvmsAPI] getDownloadUrl: endpoint removed — pending VMS migration');
  return Promise.reject(new Error('Endpoint removed — pending VMS migration'));
};

// Events API
export const getEvents = async (
  siteId: number,
  filters?: EventFilters
): Promise<{ data: { events: VMSEvent[]; counts: EventCounts } }> => {
  const { data } = await canvmsApi.get(`/sites/${siteId}/events`, { params: filters });
  return data;
};

export const getEvent = async (eventId: number): Promise<{ data: VMSEvent }> => {
  const { data } = await canvmsApi.get(`/events/${eventId}`);
  return data;
};

export const syncEvents = async (_siteId: number): Promise<{ data: {
  message: string;
  synced: number;
  errors: number;
  cameras_processed: number;
} }> => {
  console.warn('[CANvmsAPI] syncEvents: endpoint removed — pending VMS migration');
  return Promise.reject(new Error('Endpoint removed — pending VMS migration'));
};

export const updateEventStatus = async (
  siteId: number,
  eventId: number,
  status: 'new' | 'acknowledged' | 'dismissed' | 'promoted'
): Promise<{ data: VMSEvent }> => {
  const { data } = await canvmsApi.put(`/sites/${siteId}/events/${eventId}`, { status });
  return data;
};

export const bulkUpdateEventStatus = async (
  siteId: number,
  eventIds: number[],
  status: 'new' | 'acknowledged' | 'dismissed' | 'promoted'
): Promise<{ data: { updated: number } }> => {
  const { data } = await canvmsApi.put(`/sites/${siteId}/events/bulk`, { event_ids: eventIds, status });
  return data;
};

// Cross-site Events API
export const getAllEvents = async (
  filters?: CrossSiteEventFilters
): Promise<{ data: { events: VMSEvent[]; counts: EventCounts } }> => {
  const { data } = await canvmsApi.get('/events', { params: filters });
  return data;
};

export const updateEventById = async (
  eventId: number,
  status: 'new' | 'acknowledged' | 'dismissed' | 'promoted'
): Promise<{ data: VMSEvent }> => {
  const { data } = await canvmsApi.put(`/events/${eventId}/status`, { status });
  return data;
};

export const bulkUpdateEventsById = async (
  eventIds: number[],
  status: 'new' | 'acknowledged' | 'dismissed' | 'promoted'
): Promise<{ data: { updated: number } }> => {
  const { data } = await canvmsApi.put('/events/bulk', { event_ids: eventIds, status });
  return data;
};

// Customers API
export const getCustomers = async (): Promise<{ data: { customers: Customer[] } }> => {
  const { data } = await canvmsApi.get('/customers');
  return data;
};

export const getAddressesByCustomer = async (
  customerId: number
): Promise<{ data: { addresses: CustomerAddress[] } }> => {
  const { data } = await canvmsApi.get(`/customers/${customerId}/addresses`);
  return data;
};

// Admin Sites API (includes disabled sites)
export const getAllSites = async (): Promise<{ data: { sites: VMSSite[] } }> => {
  const { data } = await canvmsApi.get('/sites/all');
  return data;
};

// Users API (for access management)
export const getUsers = async (search?: string): Promise<{ data: { users: VMSUser[] } }> => {
  const params = search ? { search } : {};
  const { data } = await canvmsApi.get('/users', { params });
  return data;
};

// Site Access API
export const getSiteAccess = async (siteId: number): Promise<{ data: { access: SiteAccessRecord[] } }> => {
  const { data } = await canvmsApi.get(`/sites/${siteId}/access`);
  return data;
};

export const grantUserAccess = async (
  siteId: number,
  userId: number,
  permissions: { can_view?: boolean; can_playback?: boolean; can_manage?: boolean }
): Promise<{ data: SiteAccessRecord }> => {
  const { data } = await canvmsApi.post(`/sites/${siteId}/access`, { user_id: userId, ...permissions });
  return data;
};

export const revokeUserAccess = async (siteId: number, userId: number): Promise<{ data: { message: string } }> => {
  const { data } = await canvmsApi.delete(`/sites/${siteId}/access/${userId}`);
  return data;
};

// PTZ Control API
export const sendPTZCommand = async (
  _siteId: number,
  _cameraId: number,
  _command: PTZCommand,
  _speed?: number
): Promise<{ data: { success: boolean; command: string; speed?: number } }> => {
  console.warn('[CANvmsAPI] sendPTZCommand: endpoint removed — pending VMS migration');
  return Promise.reject(new Error('Endpoint removed — pending VMS migration'));
};

// Export API functions
export const exportEventsCSV = async (
  siteId: number,
  filters?: EventFilters
): Promise<Blob> => {
  const response = await canvmsApi.get(`/sites/${siteId}/events/export`, {
    params: filters,
    responseType: 'blob',
  });
  return response.data;
};

export const exportRecordingsCSV = async (
  _siteId: number,
  _cameraId: number,
  _startDate?: string,
  _endDate?: string
): Promise<Blob> => {
  console.warn('[CANvmsAPI] exportRecordingsCSV: endpoint removed — pending VMS migration');
  return Promise.reject(new Error('Endpoint removed — pending VMS migration'));
};

// Download helper
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Camera Sets API
export const getCameraSets = async (): Promise<{ data: { sets: CameraSet[] } }> => {
  const { data } = await canvmsApi.get('/camera-sets');
  return data;
};

export const getCameraSet = async (setId: number): Promise<{ data: CameraSet }> => {
  const { data } = await canvmsApi.get(`/camera-sets/${setId}`);
  return data;
};

export const createCameraSet = async (setData: CameraSetCreateInput): Promise<{ data: CameraSet }> => {
  const { data } = await canvmsApi.post('/camera-sets', setData);
  return data;
};

export const updateCameraSet = async (
  setId: number,
  setData: CameraSetUpdateInput
): Promise<{ data: CameraSet }> => {
  const { data } = await canvmsApi.put(`/camera-sets/${setId}`, setData);
  return data;
};

export const deleteCameraSet = async (setId: number): Promise<{ data: { message: string } }> => {
  const { data } = await canvmsApi.delete(`/camera-sets/${setId}`);
  return data;
};

export const getCamerasForSets = async (): Promise<{ data: { cameras: CameraForSelection[] } }> => {
  const { data } = await canvmsApi.get('/camera-sets/available-cameras');
  return data;
};
